import { STORAGE_NAMESPACE, TUNABLES } from "../config";
import { LANGUAGES, type Language, NAME_WORDS, type NameWords } from "../i18n";

/**
 * The visitor's name on the front page (B3.1): generated at the first visit, kept through the
 * platform's data store, changed by typing or rolled again with the dice. Pure, so it is
 * unit-tested in Node.
 *
 * A generated name is a mood and a noun in the page's language (L10N2), "Grumpy Badger",
 * "Ворчливый Барсук", "Tejón Gruñón", from the words of `src/i18n/text/names.ts`. 64 by 64 gives
 * 4096 names a language, sized for the front page: two names in a duel match once in 4096, a
 * 20-name lobby list holds a pair about once in 22 (190 pairs / 4096), ten rolls of the dice
 * repeat about once in 90; a longer list only dilutes the jokes. A generated name the visitor
 * kept follows the language: the same mood and noun by their place in the lists, so a switch
 * there and back gives the name back. A typed name is the visitor's and never changes.
 */

/** Where the platform's data store keeps the name: outside the save. */
export const NAME_KEY = `${STORAGE_NAMESPACE}:name`;

/** Where a generated name's words stand in its language's lists. */
interface NamePick {
  readonly mood: number;
  readonly noun: number;
}

/** A language's nouns in one list, the masculine first: a pick's noun is its place here. */
function nounsOf(words: NameWords): readonly string[] {
  return [...words.masculine, ...words.feminine];
}

/** The name a pick gives in `language`; a place past a shorter list wraps round it. */
function nameOf(language: Language, pick: NamePick): string {
  const words = NAME_WORDS[language];
  const nouns = nounsOf(words);
  const nounAt = pick.noun % nouns.length;
  const noun = nouns[nounAt] ?? "";
  const [masculine = "", feminine] = (words.moods[pick.mood % words.moods.length] ?? "").split("|");
  const mood = nounAt >= words.masculine.length ? (feminine ?? masculine) : masculine;
  return words.moodFirst ? `${mood} ${noun}` : `${noun} ${mood}`;
}

/** A mood and a noun in `language`, from one draw in [0, 1): the same draw gives the same name. */
export function generatedName(draw: number, language: Language): string {
  const words = NAME_WORDS[language];
  const combinations = words.moods.length * nounsOf(words).length;
  const pick = Math.min(combinations - 1, Math.max(0, Math.floor(draw * combinations)));
  return nameOf(language, {
    mood: pick % words.moods.length,
    noun: Math.floor(pick / words.moods.length),
  });
}

/** Where `name` stands in the lists of a language that generates it; undefined for a typed one. */
function pickOf(name: string): NamePick | undefined {
  const parts = name.split(" ");
  if (parts.length !== 2) {
    return undefined;
  }
  for (const language of LANGUAGES) {
    const words = NAME_WORDS[language];
    const [moodWord, nounWord] = words.moodFirst ? parts : [parts[1], parts[0]];
    const noun = nounsOf(words).indexOf(nounWord ?? "");
    const mood = words.moods.findIndex((forms) => forms.split("|").includes(moodWord ?? ""));
    if (noun >= 0 && mood >= 0 && nameOf(language, { mood, noun }) === name) {
      return { mood, noun };
    }
  }
  return undefined;
}

/**
 * A generated name said in `language`: "Grumpy Badger" becomes "Ворчливый Барсук" in Russian. A
 * name no list makes, a typed one, is the visitor's own and stays as it is.
 */
export function nameInLanguage(name: string, language: Language): string {
  const pick = pickOf(name);
  return pick === undefined ? name : nameOf(language, pick);
}

/** The dice: a new generated name in `language`, never the one it replaces. */
export function rerolledName(current: string, draw: () => number, language: Language): string {
  let name = generatedName(draw(), language);
  while (name === current) {
    name = generatedName(draw(), language);
  }
  return name;
}

/** "Squire 771": the shape the first generator (B3.1) gave, a calling and a number to 999. */
const RETIRED_NAME =
  /^(Spear|Sword|Archer|Rider|Banner|Warden|Herald|Mason|Scout|Reeve|Steward|Squire) [1-9][0-9]{0,2}$/;

/** A kept name the first generator made, which the visitor never chose: it gives way to a fun one. */
export function isRetiredName(name: string): boolean {
  return RETIRED_NAME.test(name);
}

/**
 * The visitor's kept name in `language`, else a new one, kept at once so the next visit greets
 * the same name. A kept "Squire 771" of the first generator was never the visitor's choice, so it
 * gives way; a kept generated name is said in the page's language (L10N2), unless it is the name
 * the signed-in account holds (`held`, NM2), which is the account's in every language. The front
 * page and a room's link opened late (K4) both start from it.
 */
export function keptName(
  data: { get(key: string): string | null; set(key: string, value: string): void },
  draw: () => number,
  language: Language,
  held?: string,
): string {
  const kept = cleanName(data.get(NAME_KEY) ?? "");
  if (kept !== undefined && kept === held) {
    return kept;
  }
  if (kept !== undefined && !isRetiredName(kept)) {
    const spoken = nameInLanguage(kept, language);
    if (spoken !== kept) {
      data.set(NAME_KEY, spoken);
    }
    return spoken;
  }
  const made = generatedName(draw(), language);
  data.set(NAME_KEY, made);
  return made;
}

/** The name as typed: trimmed, its spaces collapsed and cut to length; undefined when nothing is left. */
export function cleanName(text: string): string | undefined {
  const cleaned = text.trim().replace(/\s+/g, " ").slice(0, TUNABLES.landing.nameMaxLength).trim();
  return cleaned.length > 0 ? cleaned : undefined;
}
